import { Instant, LocalDateTime, ZoneId } from 'js-joda';
import { createSelector } from 'reselect';

import * as Service from 'types/service';
import { WalkInCalendarParty } from 'types';
import { isPartyCanceled, isPartyNoShow, isPartyWalkIn } from 'utils/partyUtils';
import { roundTo15MinInterval } from 'utils/timeUtils';
import { size } from 'es-toolkit/compat';
import { timeZoneSelector } from 'selectors/timeZoneSelector';
import calendarItems from 'utils/calendarItems';
import partiesSelector from 'selectors/service/partiesSelector';

/**
 * Selector for parties to display in calendar that come from walk-ins.
 */
export default createSelector(
  timeZoneSelector,
  partiesSelector,
  (timeZone: string, parties: Service.Party[]): WalkInCalendarParty[] => {
    if (size(parties) === 0) {
      return [];
    }

    // filtering first for walkins to get the correct type.
    const seatedWalkinParties = parties
      .filter(isPartyWalkIn)
      .filter((party: Service.WalkInParty) => {
        if (!party.seatedTime || isPartyNoShow(party) || isPartyCanceled(party)) {
          return false;
        }
        return true;
      });

    if (size(seatedWalkinParties) === 0) {
      return [];
    }

    return seatedWalkinParties.map((party: Service.WalkInParty) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const seatedTimeInstant = Instant.ofEpochSecond(party.seatedTime!, 0);
      const seatedLocalDateTime = LocalDateTime.ofInstant(seatedTimeInstant, ZoneId.of(timeZone));
      const dateTime15MinuteInterval = roundTo15MinInterval(seatedLocalDateTime).toString();
      const calendarItem = calendarItems.computeCalendarItemsForWalkInParty(
        party,
        dateTime15MinuteInterval
      );
      return {
        party,
        calendarItem,
        dateTime15MinuteInterval,
        type: 'WalkIn',
      };
    });
  }
);
